import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext'; // Needed for authenticated requests

// 1. Create the Context
const SMSRelayContext = createContext();

// 2. Create the Custom Hook
export const useSMSRelay = () => {
    const context = useContext(SMSRelayContext);
    if (!context) {
        throw new Error('useSMSRelay must be used within an SMSRelayProvider');
    }
    return context;
};

// 3. Create the Provider Component
export const SMSRelayProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // State for relay data
    const [devices, setDevices] = useState([]);
    const [stats, setStats] = useState(null);
    const [messages, setMessages] = useState([]);

    // Get the authenticated request function from the Auth context
    const { makeAuthenticatedRequest } = useAuth();

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090';

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const contentType = response.headers.get("content-type");
        let data = {};
        if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
        }

        if (!response.ok) {
            if (response.status === 400) {
                throw new Error(data.error || 'Invalid request.');
            } else if (response.status === 404) {
                throw new Error(data.error || 'Resource not found.');
            } else if (response.status === 429) {
                throw new Error(data.error || 'Too many requests. Please wait.');
            } else if (response.status === 500) {
                throw new Error(data.error || 'Internal server error.');
            }
            throw new Error(data.error || 'An unexpected error occurred.');
        }
        return data;
    };

    const clearMessages = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);

    // =========================================================================
    // Queue a new SMS message for relay
    // =========================================================================
    const queueSMSMessage = useCallback(async (recipient, body, purpose = null, referenceId = null) => {
        // Validate phone number (basic validation)
        if (!recipient || recipient.trim().length < 10) {
            const validationError = 'A valid phone number is required (at least 10 digits).';
            setError(validationError);
            throw new Error(validationError);
        }

        if (!body || body.trim().length === 0) {
            const validationError = 'Message body is required.';
            setError(validationError);
            throw new Error(validationError);
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const url = `${API_BASE_URL}/admin/sms-relay/api/admin/queue`;
            const requestBody = { 
                recipient: recipient.trim(),
                body: body.trim(),
            };

            // Add optional fields if provided
            if (purpose) {
                requestBody.purpose = purpose;
            }
            if (referenceId) {
                requestBody.reference_id = referenceId;
            }

            const response = await makeAuthenticatedRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            const successMessage = 'SMS message queued successfully.';
            setSuccess(successMessage);

            return {
                success: true,
                message: successMessage,
                messageId: result.message_id,
                status: result.status,
                recipient: result.recipient,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to queue SMS message.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Send verification SMS (convenience method)
    // =========================================================================
    const sendVerificationSMS = useCallback(async (phoneNumber, verificationCode) => {
        const body = `Su código de verificación es: ${verificationCode}`;
        return queueSMSMessage(phoneNumber, body, 'verification', null);
    }, [queueSMSMessage]);

    // =========================================================================
    // Send phone verification SMS using existing phone-verification endpoint
    // This uses the existing /phone-verification/send-code endpoint
    // The code is generated server-side and stored in business_registrations
    // =========================================================================
    const sendPhoneVerificationCode = useCallback(async (businessRegistryNumber, phoneNumber) => {
        // Validate inputs
        if (!businessRegistryNumber) {
            const validationError = 'Business registry number is required.';
            setError(validationError);
            throw new Error(validationError);
        }

        if (!phoneNumber || phoneNumber.trim().length < 10) {
            const validationError = 'A valid phone number is required (at least 10 digits).';
            setError(validationError);
            throw new Error(validationError);
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Use the existing phone-verification endpoint
            const url = `${API_BASE_URL}/admin/phone-verification/send-code`;
            const requestBody = { 
                business_registry_number: businessRegistryNumber,
                phone_number: phoneNumber.trim(),
            };

            const response = await makeAuthenticatedRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            const successMessage = result.message || 'Código de verificación enviado exitosamente.';
            setSuccess(successMessage);

            return {
                success: true,
                message: successMessage,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to send verification SMS.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Verify phone code using existing phone-verification endpoint
    // =========================================================================
    const verifyPhoneCode = useCallback(async (businessRegistryNumber, code) => {
        if (!businessRegistryNumber) {
            const validationError = 'Business registry number is required.';
            setError(validationError);
            throw new Error(validationError);
        }

        if (!code || code.length !== 6) {
            const validationError = 'A valid 6-digit code is required.';
            setError(validationError);
            throw new Error(validationError);
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const url = `${API_BASE_URL}/admin/phone-verification/verify-code`;
            const requestBody = { 
                business_registry_number: businessRegistryNumber,
                code: code,
            };

            const response = await makeAuthenticatedRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            const successMessage = result.message || 'Teléfono verificado exitosamente.';
            setSuccess(successMessage);

            return {
                success: true,
                message: successMessage,
                verified: true,
            };

        } catch (err) {
            const errorMessage = err.message || 'Código de verificación inválido.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Generate and send verification SMS (backend generates code)
    // Returns the generated code so admin can see it
    // FOR ADMIN USE: Backend generates code and returns it to admin
    // =========================================================================
    const sendVerificationSMSWithCode = useCallback(async (phoneNumber, businessName = null) => {
        // Validate phone number (basic validation)
        if (!phoneNumber || phoneNumber.trim().length < 10) {
            const validationError = 'A valid phone number is required (at least 10 digits).';
            setError(validationError);
            throw new Error(validationError);
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Call the backend endpoint that generates and returns the code
            const url = `${API_BASE_URL}/admin/sms-relay/api/admin/verification/send`;
            const requestBody = { 
                phone_number: phoneNumber.trim(),
            };

            // Add business name if provided
            if (businessName) {
                requestBody.business_name = businessName;
            }

            const response = await makeAuthenticatedRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            const successMessage = result.message || 'Código de verificación enviado exitosamente.';
            setSuccess(successMessage);

            return {
                success: true,
                message: successMessage,
                messageId: result.message_id,
                phoneNumber: result.phone_number,
                verificationCode: result.verification_code, // Code returned from backend
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to send verification SMS.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Get all active relay devices
    // =========================================================================
    const getRelayDevices = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const url = `${API_BASE_URL}/admin/sms-relay/api/admin/devices`;

            const response = await makeAuthenticatedRequest(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const result = await handleApiResponse(response);
            
            setDevices(result.devices || []);

            return {
                success: true,
                devices: result.devices || [],
                count: result.count || 0,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch relay devices.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Get relay system statistics
    // =========================================================================
    const getRelayStats = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const url = `${API_BASE_URL}/admin/sms-relay/api/admin/stats`;

            const response = await makeAuthenticatedRequest(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const result = await handleApiResponse(response);
            
            setStats(result.stats || null);

            return {
                success: true,
                stats: result.stats,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch relay statistics.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Get a specific message by ID
    // =========================================================================
    const getMessageById = useCallback(async (messageId) => {
        if (!messageId) {
            const validationError = 'Message ID is required.';
            setError(validationError);
            throw new Error(validationError);
        }

        setLoading(true);
        setError(null);

        try {
            const url = `${API_BASE_URL}/admin/sms-relay/api/admin/message?id=${messageId}`;

            const response = await makeAuthenticatedRequest(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const result = await handleApiResponse(response);

            return {
                success: true,
                message: result,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch message.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Trigger cleanup of old/stale messages
    // =========================================================================
    const triggerCleanup = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const url = `${API_BASE_URL}/admin/sms-relay/api/admin/cleanup`;

            const response = await makeAuthenticatedRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const result = await handleApiResponse(response);
            
            const successMessage = `Cleanup completed. Deleted: ${result.deleted_count}, Requeued: ${result.requeued_count}`;
            setSuccess(successMessage);

            return {
                success: true,
                message: successMessage,
                deletedCount: result.deleted_count,
                requeuedCount: result.requeued_count,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to trigger cleanup.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // =========================================================================
    // Refresh all data (devices and stats)
    // =========================================================================
    const refreshRelayData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [devicesResult, statsResult] = await Promise.all([
                getRelayDevices(),
                getRelayStats(),
            ]);

            return {
                success: true,
                devices: devicesResult.devices,
                stats: statsResult.stats,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to refresh relay data.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [getRelayDevices, getRelayStats]);

    // =========================================================================
    // Check if any relay device is online (helper)
    // =========================================================================
    const isRelayOnline = useCallback(() => {
        if (!devices || devices.length === 0) return false;
        
        // Check if any device has a recent heartbeat (within last 2 minutes)
        const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
        
        return devices.some(device => {
            if (!device.last_heartbeat) return false;
            const lastHeartbeat = new Date(device.last_heartbeat);
            return lastHeartbeat > twoMinutesAgo && device.is_active;
        });
    }, [devices]);

    // =========================================================================
    // Get relay health status (helper)
    // =========================================================================
    const getRelayHealthStatus = useCallback(() => {
        if (!devices || devices.length === 0) {
            return {
                status: 'offline',
                message: 'No relay devices registered',
                color: 'red',
            };
        }

        const onlineDevices = devices.filter(device => {
            if (!device.last_heartbeat || !device.is_active) return false;
            const lastHeartbeat = new Date(device.last_heartbeat);
            const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
            return lastHeartbeat > twoMinutesAgo;
        });

        if (onlineDevices.length === 0) {
            return {
                status: 'offline',
                message: 'All relay devices are offline',
                color: 'red',
            };
        }

        // Check battery levels
        const lowBatteryDevices = onlineDevices.filter(d => d.battery_level < 20);
        if (lowBatteryDevices.length > 0) {
            return {
                status: 'warning',
                message: `${onlineDevices.length} device(s) online, ${lowBatteryDevices.length} with low battery`,
                color: 'yellow',
            };
        }

        return {
            status: 'online',
            message: `${onlineDevices.length} relay device(s) online`,
            color: 'green',
        };
    }, [devices]);

    // Define the context value
    const value = {
        // State
        loading,
        error,
        success,
        devices,
        stats,
        messages,
        
        // Actions - Phone Verification (uses existing endpoints)
        sendPhoneVerificationCode,
        verifyPhoneCode,
        
        // Actions - SMS Relay (new sms_relay_context)
        queueSMSMessage,
        sendVerificationSMS,
        sendVerificationSMSWithCode,
        getRelayDevices,
        getRelayStats,
        getMessageById,
        triggerCleanup,
        refreshRelayData,
        clearMessages,
        
        // Helpers
        isRelayOnline,
        getRelayHealthStatus,
    };

    return (
        <SMSRelayContext.Provider value={value}>
            {children}
        </SMSRelayContext.Provider>
    );
};

export default SMSRelayContext;